/**
 * Tag display helpers — shared between the polish detail page and
 * brand-page item cards so both surfaces render structured tag values
 * the same way.
 *
 * Two moving parts:
 *   1. `canonicalize(raw, canonical)` — keeps only values that appear
 *      (case-insensitively) in the category's canonical enum. Filters
 *      out junk that landed in the wrong field (e.g. "Buildable"
 *      pushed into `finish[]` via a misdirected Tag Pusher, or
 *      lowercase duplicates from a fresh Tag Pusher push next to an
 *      old capitalized push).
 *   2. `titleCase(v)` — canonical enum values are lowercase strings
 *      like "shimmer" or "linear holo". We keep them lowercase in
 *      Firestore but title-case each word for display so viewers see
 *      "Shimmer" and "Linear Holo" everywhere.
 *
 * Raw data is left untouched — filtering is a display-time concern.
 * Migrating misfiled values happens via Tag Pusher; the display just
 * hides them until it's cleaned.
 */

/**
 * Title-case a canonical enum string. Enum values are lowercase (e.g.
 * "shimmer", "linear holo", "quick-dry") and stored that way; we
 * upper-case each word for display without touching the raw string
 * so lookups against the canonical set still work.
 */
export function titleCaseTag(v: string): string {
  return v
    .split(/([\s-])/)
    .map((part) =>
      part.length > 0 && /[a-z]/i.test(part[0])
        ? part[0].toUpperCase() + part.slice(1)
        : part
    )
    .join('');
}

/**
 * Keep only values that appear (case-insensitively) in the canonical
 * enum. Returns them in canonical (lowercase) form so downstream
 * `titleCaseTag` is deterministic. De-dupes case-insensitively too
 * so "Magnetic" and "magnetic" don't both survive as two chips.
 */
export function canonicalizeTags(
  raw: readonly string[] | undefined,
  canonical: readonly string[]
): string[] {
  if (!raw || raw.length === 0) return [];
  const canonicalLower = new Map<string, string>();
  canonical.forEach((c) => canonicalLower.set(c.toLowerCase(), c));
  const kept: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    if (typeof r !== 'string') continue;
    const match = canonicalLower.get(r.trim().toLowerCase());
    if (match && !seen.has(match)) {
      seen.add(match);
      kept.push(match);
    }
  }
  return kept;
}
